import { useState, useEffect, useMemo, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiDollarSign,
  FiCheckCircle,
  FiAlertTriangle,
  FiClock,
  FiList,
  FiArchive,
  FiUpload,
  FiRefreshCw,
  FiFilter,
  FiCreditCard,
  FiCheckSquare,
  FiEye,
  FiAlertCircle,
  FiX,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import reconciliationService from "../../../services/reconciliationService";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

export default function AccountantReconciliation() {
  const { user, allColleges, selectedCollegeId: globalCollegeId, selectCollege } = useApp();

  // Multi-college support
  const collegesList = useMemo(() => {
    if (Array.isArray(allColleges) && allColleges.length > 0) {
      return allColleges;
    }
    const list = [];
    if (user?.linkedCollege) {
      list.push(user.linkedCollege);
    }
    if (Array.isArray(user?.assignedColleges)) {
      user.assignedColleges.forEach((c) => {
        if (c && !list.some((existing) => (existing._id || existing) === (c._id || c))) {
          list.push(c);
        }
      });
    }
    return list;
  }, [user, allColleges]);

  const [selectedCollegeId, setSelectedCollegeId] = useState(() => {
    return globalCollegeId && globalCollegeId !== "all" ? globalCollegeId : "ALL";
  });

  useEffect(() => {
    if (globalCollegeId) {
      setSelectedCollegeId(globalCollegeId === "all" ? "ALL" : globalCollegeId);
    }
  }, [globalCollegeId]);
  const [activeTab, setActiveTab] = useState("pending"); // pending | vault | deposits | audit

  const [summaryStats, setSummaryStats] = useState(null);
  const [pendingHandovers, setPendingHandovers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Verification Modal
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [verifiedAmount, setVerifiedAmount] = useState("");
  const [accountantRemarks, setAccountantRemarks] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Bank Deposit Modal
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [selectedForDeposit, setSelectedForDeposit] = useState([]); // handover IDs
  const [depositForm, setDepositForm] = useState({
    collegeId: "",
    bankName: "",
    bankAccountNumber: "",
    challanSlipNo: "",
    depositSlipUrl: "",
    amountDeposited: "",
    depositDate: new Date().toISOString().slice(0, 10),
    delayReason: "",
    remarks: "",
  });
  const [depositing, setDepositing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCollegeId && selectedCollegeId !== "ALL") {
        params.college = selectedCollegeId;
      }

      const [statsRes, handoversRes, depositsRes, auditRes] = await Promise.allSettled([
        reconciliationService.getDashboardSummaryStats(params),
        reconciliationService.getPendingHandovers(params),
        reconciliationService.getDeposits(params),
        reconciliationService.getAuditLogs(params),
      ]);

      if (statsRes.status === "fulfilled") setSummaryStats(statsRes.value.data);
      if (handoversRes.status === "fulfilled") setPendingHandovers(handoversRes.value.data || []);
      if (depositsRes.status === "fulfilled") setDeposits(depositsRes.value.data || []);
      if (auditRes.status === "fulfilled") setAuditLogs(auditRes.value.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load reconciliation data");
    } finally {
      setLoading(false);
    }
  }, [selectedCollegeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handovers ready for bank deposit (status == "Received" or "Discrepancy_Noted", and not yet deposited)
  const vaultHandovers = useMemo(() => {
    return pendingHandovers.filter(
      (h) => (h.status === "Received" || h.status === "Discrepancy_Noted") && !h.bankDeposit
    );
  }, [pendingHandovers]);

  // Unverified handovers submitted by clerks
  const unverifiedHandovers = useMemo(() => {
    return pendingHandovers.filter((h) => h.status === "Submitted");
  }, [pendingHandovers]);

  // Open Verify Modal
  const handleOpenVerify = (handover) => {
    setSelectedHandover(handover);
    setVerifiedAmount(String(handover.actualAmount));
    setAccountantRemarks("");
    setVerifyModalOpen(true);
  };

  const handleConfirmReceipt = async () => {
    if (!selectedHandover) return;
    setVerifying(true);
    try {
      await reconciliationService.receiveHandover(selectedHandover._id, {
        verifiedAmount: Number(verifiedAmount),
        accountantRemarks,
      });
      toast.success(`Handover ${selectedHandover.handoverNo} marked as verified and received!`);
      setVerifyModalOpen(false);
      setSelectedHandover(null);
      loadData();
    } catch (err) {
      toast.error(err.message || "Failed to verify handover.");
    } finally {
      setVerifying(false);
    }
  };

  // Open Deposit Modal with selected handovers
  const handleOpenDepositModal = () => {
    if (selectedForDeposit.length === 0) {
      toast.error("Please select at least one received cash handover to deposit.");
      return;
    }

    const chosen = vaultHandovers.filter((h) => selectedForDeposit.includes(h._id));
    if (chosen.length === 0) return;

    // Check college uniformity
    const firstColId = (chosen[0].college?._id || chosen[0].college).toString();
    const isSingleCollege = chosen.every(
      (h) => (h.college?._id || h.college).toString() === firstColId
    );
    if (!isSingleCollege) {
      toast.error("All selected handovers for a single bank deposit must belong to the SAME college.");
      return;
    }

    const totalCash = chosen.reduce((s, h) => s + (Number(h.actualAmount) || 0), 0);

    // Resolve college bank accounts if available
    const collegeObj = collegesList.find((c) => (c._id || c).toString() === firstColId);
    const defaultBank = collegeObj?.bankAccounts?.find((b) => b.isDefault) || collegeObj?.bankAccounts?.[0];

    setDepositForm({
      collegeId: firstColId,
      bankName: defaultBank?.bankName || "",
      bankAccountNumber: defaultBank?.accountNumber || "",
      challanSlipNo: "",
      depositSlipUrl: "",
      amountDeposited: String(totalCash),
      depositDate: new Date().toISOString().slice(0, 10),
      delayReason: "",
      remarks: "",
    });

    setDepositModalOpen(true);
  };

  // Compute delay days for selected deposit bundle
  const depositDelayInfo = useMemo(() => {
    if (!depositModalOpen || selectedForDeposit.length === 0) return { days: 0, isDelayed: false };
    const chosen = vaultHandovers.filter((h) => selectedForDeposit.includes(h._id));
    if (!chosen.length) return { days: 0, isDelayed: false };

    const dates = chosen.map((h) => new Date(h.handoverDate || h.createdAt).getTime());
    const earliest = Math.min(...dates);
    const depTime = new Date(depositForm.depositDate).getTime();
    const diff = depTime - earliest;
    const days = Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
    return { days, isDelayed: days > 1 };
  }, [depositModalOpen, selectedForDeposit, vaultHandovers, depositForm.depositDate]);

  const handleConfirmDeposit = async (e) => {
    e.preventDefault();
    if (!depositForm.bankName || !depositForm.bankAccountNumber || !depositForm.challanSlipNo) {
      toast.error("Bank name, account number, and challan/slip number are mandatory.");
      return;
    }

    if (depositDelayInfo.isDelayed && !depositForm.delayReason.trim()) {
      toast.error(`This deposit is delayed by ${depositDelayInfo.days} days. Delay reason is mandatory.`);
      return;
    }

    setDepositing(true);
    try {
      await reconciliationService.recordDeposit({
        collegeId: depositForm.collegeId,
        handoverIds: selectedForDeposit,
        bankName: depositForm.bankName,
        bankAccountNumber: depositForm.bankAccountNumber,
        challanSlipNo: depositForm.challanSlipNo,
        depositSlipUrl: depositForm.depositSlipUrl,
        amountDeposited: Number(depositForm.amountDeposited),
        depositDate: depositForm.depositDate,
        delayReason: depositForm.delayReason,
        remarks: depositForm.remarks,
      });

      toast.success("Bank deposit recorded and reconciled successfully!");
      setDepositModalOpen(false);
      setSelectedForDeposit([]);
      loadData();
    } catch (err) {
      toast.error(err.message || "Failed to record bank deposit.");
    } finally {
      setDepositing(false);
    }
  };

  const toggleSelectAllVault = (e) => {
    if (e.target.checked) {
      setSelectedForDeposit(vaultHandovers.map((h) => h._id));
    } else {
      setSelectedForDeposit([]);
    }
  };

  const toggleSelectHandover = (id) => {
    setSelectedForDeposit((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner" />
        Loading Accounts Cash Reconciliation Desk…
      </div>
    );
  }

  const hSummary = summaryStats?.handoverSummary || {};
  const dSummary = summaryStats?.depositSummary || {};

  return (
    <div>
      {/* ── Page Header & Multi-College Selector ── */}
      <div className="page-header" style={{ marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 className="page-title">Accounts Cash Desk &amp; Bank Reconciliation 🏦</h1>
          <p className="page-subtitle">
            Manage clerk handovers, physical cash vault, and college bank deposits with zero discrepancy.
          </p>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
          {/* Multi-College Dropdown */}
          {collegesList.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)" }}>College:</span>
              <select
                className="form-control"
                value={selectedCollegeId}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedCollegeId(val);
                  selectCollege(val === "ALL" ? "all" : val);
                }}
                style={{ minWidth: 190, padding: "0.35rem 0.65rem", fontSize: 13, fontWeight: 600 }}
              >
                <option value="ALL">All Campuses (Group Overview)</option>
                {collegesList.map((col) => (
                  <option key={col._id || col} value={col._id || col}>
                    {col.code ? `[${col.code}] ` : ""}
                    {col.name || "College"}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button className="btn btn-outline" onClick={loadData}>
            <FiRefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* ── KPI Summary Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "0.85rem",
          marginBottom: "1.5rem",
        }}
      >
        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #16a34a" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Cash in Accountant Vault
          </div>
          <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#16a34a", marginTop: 4 }}>
            {inr(summaryStats?.cashInAccountantVault)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            {vaultHandovers.length} batch(es) awaiting bank deposit
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #f59e0b" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Pending Verification
          </div>
          <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#f59e0b", marginTop: 4 }}>
            {unverifiedHandovers.length}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Handovers submitted by clerks awaiting receipt
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #003b67" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Total Deposited into Bank
          </div>
          <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#003b67", marginTop: 4 }}>
            {inr(dSummary.totalBankDeposited)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Across {dSummary.totalDeposits || 0} bank deposit(s)
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #ef4444" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Clerk Shortages Flagged
          </div>
          <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#ef4444", marginTop: 4 }}>
            {inr(hSummary.totalClerkShortage)}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Total excess noted: {inr(hSummary.totalClerkExcess)}
          </div>
        </div>

        <div className="card" style={{ padding: "1.1rem", borderLeft: "4px solid #8b5cf6" }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>
            Delayed Bank Deposits
          </div>
          <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "#8b5cf6", marginTop: 4 }}>
            {dSummary.totalDelayedDeposits || 0}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            All legitimate reasons recorded
          </div>
        </div>
      </div>

      {/* ── Navigation Tabs ── */}
      <div style={{ display: "flex", gap: "0.5rem", borderBottom: "2px solid var(--border, #e2e8f0)", marginBottom: "1.25rem" }}>
        {[
          { id: "pending", label: `Pending Verification (${unverifiedHandovers.length})`, icon: FiClock },
          { id: "vault", label: `Cash in Vault / Deposit Prep (${vaultHandovers.length})`, icon: FiArchive },
          { id: "deposits", label: `Bank Deposit History (${deposits.length})`, icon: FiCreditCard },
          { id: "audit", label: `Reconciliation Audit Logs (${auditLogs.length})`, icon: FiList },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: "0.6rem 1.1rem",
                border: "none",
                background: "none",
                borderBottom: isActive ? "3px solid #003b67" : "3px solid transparent",
                color: isActive ? "#003b67" : "var(--text-muted)",
                fontWeight: isActive ? 700 : 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
              }}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ── TAB 1: PENDING VERIFICATION ── */}
      {activeTab === "pending" && (
        <div className="card" style={{ padding: "1.25rem" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: 6 }}>
            <FiClock color="#f59e0b" /> Clerk Handovers Awaiting Verification
          </h3>

          {unverifiedHandovers.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2.5rem", color: "var(--text-muted)" }}>
              <FiCheckCircle size={32} color="#16a34a" style={{ marginBottom: 8 }} />
              <div>All clerk handovers have been verified and received! No items pending.</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Handover No</th>
                    <th>College</th>
                    <th>Clerk</th>
                    <th>Date</th>
                    <th>Expected</th>
                    <th>Handed Over</th>
                    <th>Difference</th>
                    <th>Clerk Remarks</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {unverifiedHandovers.map((h) => {
                    const isShortage = h.difference < 0;
                    return (
                      <tr key={h._id}>
                        <td style={{ fontWeight: 600 }}>{h.handoverNo}</td>
                        <td>
                          <span className="badge badge-info">{h.college?.code || "College"}</span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{h.clerk?.displayName || h.clerk?.username}</td>
                        <td>{h.collectionDate}</td>
                        <td>{inr(h.expectedAmount)}</td>
                        <td style={{ fontWeight: 700 }}>{inr(h.actualAmount)}</td>
                        <td>
                          {h.difference === 0 ? (
                            <span className="badge badge-success">₹0 Match</span>
                          ) : isShortage ? (
                            <span className="badge badge-danger">
                              -₹{Math.abs(h.difference).toLocaleString("en-IN")} Shortage
                            </span>
                          ) : (
                            <span className="badge badge-warning">+₹{h.difference.toLocaleString("en-IN")} Excess</span>
                          )}
                        </td>
                        <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {h.clerkRemarks || "—"}
                        </td>
                        <td>
                          <button
                            className="btn btn-primary"
                            onClick={() => handleOpenVerify(h)}
                            style={{ padding: "0.3rem 0.65rem", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}
                          >
                            <FiCheckSquare size={13} /> Verify &amp; Receive
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: CASH IN VAULT & DEPOSIT PREPARATION ── */}
      {activeTab === "vault" && (
        <div className="card" style={{ padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
            <div>
              <h3 style={{ fontSize: "1rem", fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                <FiArchive color="#16a34a" /> Verified Cash in Vault (Ready for Bank Deposit)
              </h3>
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 0" }}>
                Select handovers belonging to the same college to bundle into a single bank deposit.
              </p>
            </div>
            <button
              className="btn btn-primary"
              disabled={selectedForDeposit.length === 0}
              onClick={handleOpenDepositModal}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <FiCreditCard size={14} /> Record Bank Deposit ({selectedForDeposit.length} Selected)
            </button>
          </div>

          {vaultHandovers.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2.5rem", color: "var(--text-muted)" }}>
              No verified handovers in the vault currently. All received cash has been deposited in the bank.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={selectedForDeposit.length === vaultHandovers.length && vaultHandovers.length > 0}
                        onChange={toggleSelectAllVault}
                      />
                    </th>
                    <th>Handover No</th>
                    <th>College</th>
                    <th>Clerk</th>
                    <th>Date Received</th>
                    <th>Verified Cash</th>
                    <th>Discrepancy</th>
                    <th>Accountant Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {vaultHandovers.map((h) => {
                    const isChecked = selectedForDeposit.includes(h._id);
                    return (
                      <tr key={h._id} style={{ background: isChecked ? "rgba(0, 59, 103, 0.04)" : "inherit" }}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelectHandover(h._id)}
                          />
                        </td>
                        <td style={{ fontWeight: 600 }}>{h.handoverNo}</td>
                        <td>
                          <span className="badge badge-info">{h.college?.code || "College"}</span>
                        </td>
                        <td>{h.clerk?.displayName || h.clerk?.username}</td>
                        <td>{h.receivedAt ? new Date(h.receivedAt).toLocaleDateString("en-IN") : h.collectionDate}</td>
                        <td style={{ fontWeight: 700, color: "#16a34a" }}>{inr(h.actualAmount)}</td>
                        <td>
                          {h.difference === 0 ? (
                            <span className="badge badge-success">Balanced</span>
                          ) : h.difference < 0 ? (
                            <span className="badge badge-danger">Shortage: -₹{Math.abs(h.difference)}</span>
                          ) : (
                            <span className="badge badge-warning">Excess: +₹{h.difference}</span>
                          )}
                        </td>
                        <td>{h.accountantRemarks || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 3: BANK DEPOSIT HISTORY ── */}
      {activeTab === "deposits" && (
        <div className="card" style={{ padding: "1.25rem" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "1rem", display: "flex", alignItems: "center", gap: 6 }}>
            <FiCreditCard color="#003b67" /> Bank Deposit Ledger
          </h3>

          {deposits.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2.5rem", color: "var(--text-muted)" }}>
              No bank deposits recorded yet.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Deposit No</th>
                    <th>College</th>
                    <th>Bank &amp; Account</th>
                    <th>Deposit Date</th>
                    <th>Challan / Slip No</th>
                    <th>Cash Amount</th>
                    <th>Delay Status</th>
                    <th>Reconciliation</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((d) => (
                    <tr key={d._id}>
                      <td style={{ fontWeight: 600 }}>{d.depositNo}</td>
                      <td>
                        <span className="badge badge-info">{d.college?.code || "College"}</span>
                      </td>
                      <td>
                        <div>
                          <strong>{d.bankName}</strong>
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>A/C: {d.bankAccountNumber}</div>
                      </td>
                      <td>{new Date(d.depositDate).toLocaleDateString("en-IN")}</td>
                      <td>
                        <code>{d.challanSlipNo}</code>
                        {d.depositSlipUrl && (
                          <div style={{ marginTop: 2 }}>
                            <a
                              href={d.depositSlipUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={{ fontSize: 11, color: "#003b67" }}
                            >
                              View Slip
                            </a>
                          </div>
                        )}
                      </td>
                      <td style={{ fontWeight: 700 }}>{inr(d.amountDeposited)}</td>
                      <td>
                        {d.isDelayed ? (
                          <div>
                            <span className="badge badge-warning">Delayed ({d.delayDays}d)</span>
                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                              Reason: {d.delayReason || "Not specified"}
                            </div>
                          </div>
                        ) : (
                          <span className="badge badge-success">On-Time Deposit</span>
                        )}
                      </td>
                      <td>
                        {d.status === "Deposited" || d.status === "Reconciled" ? (
                          <span className="badge badge-success">Reconciled</span>
                        ) : (
                          <span className="badge badge-danger">Discrepancy ({inr(d.difference)})</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 4: RECONCILIATION AUDIT LOGS ── */}
      {activeTab === "audit" && (
        <div className="card" style={{ padding: "1.25rem" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "1rem", display: "flex", alignItems: "center", gap: 6 }}>
            <FiList color="#8b5cf6" /> Forensic Audit Trail
          </h3>

          {auditLogs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2.5rem", color: "var(--text-muted)" }}>
              No audit logs recorded.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>Performed By</th>
                    <th>College</th>
                    <th>Expected</th>
                    <th>Actual</th>
                    <th>Variance</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log._id}>
                      <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {new Date(log.createdAt).toLocaleString("en-IN")}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            log.actionType.includes("DISCREPANCY")
                              ? "badge-danger"
                              : log.actionType.includes("DEPOSIT")
                              ? "badge-info"
                              : "badge-success"
                          }`}
                        >
                          {log.actionType}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {log.performedBy?.displayName || log.performedBy?.username} ({log.performedByRole || "user"})
                      </td>
                      <td>{log.college?.code || "College"}</td>
                      <td>{inr(log.amountExpected)}</td>
                      <td>{inr(log.amountActual)}</td>
                      <td>
                        {log.difference === 0 ? (
                          "—"
                        ) : log.difference < 0 ? (
                          <span style={{ color: "#ef4444", fontWeight: 700 }}>
                            -₹{Math.abs(log.difference).toLocaleString("en-IN")}
                          </span>
                        ) : (
                          <span style={{ color: "#f59e0b", fontWeight: 700 }}>
                            +₹{log.difference.toLocaleString("en-IN")}
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{log.remarks || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── MODAL: VERIFY & RECEIVE HANDOVER ── */}
      {verifyModalOpen && selectedHandover && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "1rem",
          }}
        >
          <div
            className="card"
            style={{
              width: "100%",
              maxWidth: 580,
              padding: "1.75rem",
              background: "#fff",
              borderRadius: 10,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Verify &amp; Receive Cash Handover</h3>
              <button
                className="btn btn-outline"
                onClick={() => setVerifyModalOpen(false)}
                style={{ padding: "0.25rem 0.5rem" }}
              >
                <FiX size={14} />
              </button>
            </div>

            <div style={{ background: "var(--bg-hover, #f8fafc)", padding: "0.85rem", borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>
                  <strong>Handover No:</strong> {selectedHandover.handoverNo}
                </div>
                <div>
                  <strong>Clerk:</strong> {selectedHandover.clerk?.displayName || selectedHandover.clerk?.username}
                </div>
                <div>
                  <strong>Expected Amount:</strong> {inr(selectedHandover.expectedAmount)}
                </div>
                <div>
                  <strong>Clerk Handed Over:</strong> {inr(selectedHandover.actualAmount)}
                </div>
              </div>
              {selectedHandover.clerkRemarks && (
                <div style={{ marginTop: 8, fontStyle: "italic" }}>
                  <strong>Clerk Remarks:</strong> {selectedHandover.clerkRemarks}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>
                Physically Verified Cash Count (₹)
              </label>
              <input
                type="number"
                min="0"
                className="form-control"
                style={{ fontSize: "1.2rem", fontWeight: 700, padding: "0.6rem" }}
                value={verifiedAmount}
                onChange={(e) => setVerifiedAmount(e.target.value)}
                required
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 5 }}>
                Accountant Remarks / Variance Notes
              </label>
              <textarea
                className="form-control"
                rows={2}
                value={accountantRemarks}
                onChange={(e) => setAccountantRemarks(e.target.value)}
                placeholder="Notes on physical cash receipt..."
              />
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn btn-outline" onClick={() => setVerifyModalOpen(false)} disabled={verifying}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmReceipt} disabled={verifying}>
                {verifying ? "Confirming..." : "Confirm Physical Receipt"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: RECORD BANK DEPOSIT ── */}
      {depositModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "1rem",
          }}
        >
          <div
            className="card"
            style={{
              width: "100%",
              maxWidth: 640,
              maxHeight: "92vh",
              overflowY: "auto",
              padding: "1.75rem",
              background: "#fff",
              borderRadius: 10,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Record College Bank Deposit</h3>
              <button
                className="btn btn-outline"
                onClick={() => setDepositModalOpen(false)}
                style={{ padding: "0.25rem 0.5rem" }}
              >
                <FiX size={14} />
              </button>
            </div>

            <form onSubmit={handleConfirmDeposit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    Bank Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={depositForm.bankName}
                    onChange={(e) => setDepositForm({ ...depositForm, bankName: e.target.value })}
                    placeholder="e.g. State Bank of India"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    Bank Account Number <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={depositForm.bankAccountNumber}
                    onChange={(e) => setDepositForm({ ...depositForm, bankAccountNumber: e.target.value })}
                    placeholder="e.g. 40291029102"
                    required
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    Deposit Date <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={depositForm.depositDate}
                    onChange={(e) => setDepositForm({ ...depositForm, depositDate: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    Challan / Slip Reference No <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={depositForm.challanSlipNo}
                    onChange={(e) => setDepositForm({ ...depositForm, challanSlipNo: e.target.value })}
                    placeholder="e.g. CHAL-84920"
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Actual Amount Deposited into Bank (₹) <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  style={{ fontSize: "1.15rem", fontWeight: 700 }}
                  value={depositForm.amountDeposited}
                  onChange={(e) => setDepositForm({ ...depositForm, amountDeposited: e.target.value })}
                  required
                />
              </div>

              {/* Delay Warning & Mandatory Reason */}
              {depositDelayInfo.isDelayed && (
                <div
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #f59e0b",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 14,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>
                    <FiAlertCircle size={16} /> Deposit Delay Detected ({depositDelayInfo.days} Days from Clerk Handover)
                  </div>
                  <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 8px" }}>
                    Legitimate delays (e.g. Bank Holidays, Strikes, Closures) must be documented for management audit.
                  </p>

                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    Reason for Delay <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    className="form-control"
                    value={depositForm.delayReason}
                    onChange={(e) => setDepositForm({ ...depositForm, delayReason: e.target.value })}
                    required
                    style={{ marginBottom: 6 }}
                  >
                    <option value="">-- Select Legitimate Reason --</option>
                    <option value="Bank Holiday">Bank Holiday</option>
                    <option value="Weekend / Non-Banking Day">Weekend / Non-Banking Day</option>
                    <option value="Bank Strike / System Outage">Bank Strike / System Outage</option>
                    <option value="Operational Cash Verification">Operational Cash Verification</option>
                    <option value="Other Legitimate Reason">Other Legitimate Reason</option>
                  </select>
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Deposit Slip Image / PDF URL (Optional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={depositForm.depositSlipUrl}
                  onChange={(e) => setDepositForm({ ...depositForm, depositSlipUrl: e.target.value })}
                  placeholder="https://... deposit receipt photo"
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Accountant Deposit Remarks
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={depositForm.remarks}
                  onChange={(e) => setDepositForm({ ...depositForm, remarks: e.target.value })}
                  placeholder="Additional deposit notes..."
                />
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setDepositModalOpen(false)}
                  disabled={depositing}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={depositing}>
                  {depositing ? "Recording Deposit..." : "Submit Bank Deposit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
